import { useState, useCallback } from "react";

type UseChatgptHookReturn = {
  messages: string[];
  addMessage: (msg: string) => void;
  clearMessages: () => void;
  chatGPTPromptForSearch: (searchText: string) => string;
};

const useChatgptHook = (): UseChatgptHookReturn => {
  const [messages, setMessages] = useState<string[]>([]);

  const addMessage = useCallback((msg: string) => {
    setMessages((prev) => [...prev, msg]);
  }, []);

  const clearMessages = useCallback(() => {
    setMessages([]);
  }, []);

  const chatGPTPromptForSearch = useCallback((searchText: string): string => {
    const prompt = `
                You act as an intelligent parser that responds exclusively with a JSON object structured according to the user's intent. Never return all available services: only those explicitly requested or clearly inferred from the text. If languages are mentioned, return them in ISO 639-2 three-letter format under the language key, using source and/or target as indicated in the text. The response must contain one or more of the following keys: search\_for (with possible values such as 'freelancers', 'businesses', 'jobs', 'users', 'forums', 'memberships', 'opportunities'), services (such as 'Translation', 'Subtitling', 'Voice-over (dubbing)', etc.), languages, fields (such as 'Medical', 'Law/Patents', etc.), and keyword. Do not include explanations; just respond with the corresponding JSON. Valid examples:

                Input: "I'm looking for a freelance translator from English to Spanish specializing in medical texts."
                Output: "{searching_for: 'freelancers', services: ['Translation'], languages: [{source: 'eng', target: 'esl'}], fields: ['Medical']}"
                Input: "post editing french"
                Output: "{searching_for: 'freelancers', services: ['MT Post-editing'], languages: [{source: 'fra'}]}"
                Input: "voiceover in english"
                Output: "{searching_for: 'freelancers', services: ['Voiceover (dubbing)'], languages: [{source: 'eng'}]}"
                Input: "PureFluent"
                Output: "{searching_for: 'businesses', keyword: 'PureFluent'}"
                Input: "Nicolas calcagno"
                Output: "{searching_for: 'users', keyword: 'Nicolas calcagno'}"
                Input: "forums in spanish"
                Output: "{searching_for: 'forums', languages: [{target: 'esl'}]}"
                Input: "professional membership"
                Output: "{searching_for: 'memberships', keyword: 'professional'}"
                Input: "interpreter opportunities"
                Output: "{searching_for: 'opportunities', services: ['Interpreting']}"
                Input: "language assessment for Spanish"
                Output: "{searching_for: 'language_assessment', languages: [{target: 'esl'}]}"
                Input: "translation industry news"
                Output: "{searching_for: 'news', keyword: 'translation industry'}"
                Input: "edit my profile"
                Output: "{searching_for: 'profile'}"
                Input: "update my contact information"
                Output: "{searching_for: 'profile', keyword: 'contact information'}"
                Input: "change profile picture"
                Output: "{searching_for: 'profile', keyword: 'profile picture'}"
                Input: "training in translation"
                Output: "{searching_for: 'learn', keyword: 'translation'}"
                Input: "courses for subtitling"
                Output: "{searching_for: 'learn', keyword: 'subtitling'}"
                Input: "online interpreting course"
                Output: "{searching_for: 'learn', keyword: 'interpreting'}"
                Input: "información del sitio, que es proz?"
                Output: "{searching_for: 'site_info', keyword: '/about/overhaul'}"
                Input: "I want to talk with support"
                Output: "{searching_for: 'support', keyword: '/support-center'}"
                Input: "quiero saber de prozpay"
                Output: "{searching_for: 'prozpay', keywor d: '/pay/about'}"
                Input: "get paid" 
                Output: "{searching_for: 'prozpay', keyword: '/pay/get-paid'}"
                Input: "how to pay"
                Output: "{searching_for: 'prozpay', keyword: '/pay/how-to-pay'}"
                Input: "I want to know about proz.com"
                Output: "{searching_for: 'site_info', keyword: '/about/overhaul'}"
                Input: "probono"
                Output: "{searching_for: 'probono', keyword: '/probono'}"
                Input: "valunteer work"
                Output: "{searching_for: 'probono', keyword: '/probono'}"
                Input: "pastey"
                Output: "{searching_for: 'pastey', keyword: '/pastey'}"
                Input: "cafetran"
                Output: "{searching_for: 'cafetran', keyword: '/cafetran'}"
                Input: "Interpreters community"
                Output: "{searching_for: 'interpreters-community', keyword: 'Interpreters community'}
                Input: "woman in translation"
                Output: "{searching_for: 'wit-community', keyword: 'woman in translation community'}
                Input: "managed services"
                Output: "{searching_for: 'managed-services', keyword: 'managed services community'}
                Input: "I want proz handle my posted jobs"
                Output: "{searching_for: 'managed-services', keyword: 'proz handle my posted jobs'}
                



                if you don't find any of the above keys, return the more similar one for you. 

                and the new user input is: "${searchText}", so please respond with a valid JSON object in the same format as the examples above. Do not include any comments, explanations, or extra output.

            `;
    return prompt;
  }, []);

  return {
    messages,
    addMessage,
    clearMessages,
    chatGPTPromptForSearch,
  };
};

export default useChatgptHook;
